import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/PageTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { fmtDate } from "@/lib/format";
import { Plus, Trash2, Pencil, ShieldCheck, Eye } from "lucide-react";
import { toast } from "sonner";

const empty = { email: "", password: "", name: "", role: "viewonly" };

export default function Users() {
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/auth/users")).data,
    enabled: isAdmin,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (editId) {
        const body = { name: form.name, role: form.role };
        if (form.password) body.password = form.password;
        return api.put(`/auth/users/${editId}`, body);
      }
      return api.post("/auth/users", form);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(editId ? "User updated" : "User created");
      setOpen(false); setForm(empty); setEditId(null);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/auth/users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("User removed"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ email: u.email, password: "", name: u.name, role: u.role }); setEditId(u.id); setOpen(true); };

  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div>
      <PageTitle title="User Management" subtitle={`${users.length} users · admin has full access, view-only can only read`}>
        <div className="flex gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-destructive hover:bg-destructive/10" data-testid="wipe-data-button">
                <Trash2 className="h-4 w-4" /> Reset / Clean All Data
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clean All Business & Test Data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all suppliers, purchases, payments, expenses, employees, salaries, products, and audit logs. Numbering counters will restart from SUP0001, PUR00001, etc. Your admin login will be kept.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    try {
                      await api.post("/auth/wipe-database");
                      qc.invalidateQueries();
                      toast.success("All data cleared successfully! Numbering reset to 0.");
                    } catch (e) {
                      toast.error("Failed to clean data");
                    }
                  }}
                  className="bg-destructive text-destructive-foreground"
                >
                  Confirm Clean Slate
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button onClick={openNew} data-testid="add-user-button"><Plus className="h-4 w-4" /> Add User</Button>
        </div>
      </PageTitle>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto ssacc-scroll">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
                <TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium">{u.name}{u.id === user?.id && <span className="text-xs text-muted-foreground"> (you)</span>}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell>
                    {u.role === "admin"
                      ? <Badge className="gap-1"><ShieldCheck className="h-3 w-3" /> Admin</Badge>
                      : <Badge variant="secondary" className="gap-1"><Eye className="h-3 w-3" /> View Only</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}><Pencil className="h-4 w-4" /></Button>
                      {u.id !== user?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="text-destructive" data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>This user will no longer be able to sign in.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del.mutate(u.id)} className="bg-destructive text-destructive-foreground">Remove</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit User" : "Add User"}</DialogTitle>
            <DialogDescription>{editId ? "Update name, role, or reset the password (leave blank to keep)." : "Create a login. View-only users can see everything but cannot make changes."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={set("email")} disabled={!!editId} className="mt-1.5" data-testid="user-email" />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="user-name" />
            </div>
            <div>
              <Label>{editId ? "New Password (optional)" : "Password *"}</Label>
              <Input type="password" value={form.password} onChange={set("password")} placeholder={editId ? "Leave blank to keep current" : "Min 6 characters"} className="mt-1.5" data-testid="user-password" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — full access</SelectItem>
                  <SelectItem value="viewonly">View Only — read only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!form.email || (!editId && !form.password) || save.isPending} data-testid="save-user">
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
